import Box from '@mui/material/Box';
import { styled, useThemeProps } from '@mui/material/styles';
import PropTypes from 'prop-types';
import React from 'react';

/**
 * Fluid Grid Component
 * 
 * **Important Notes:**
 * - Responsive objects use Material-UI breakpoint keys (xs, sm, ms, md, lg, xl)
 * - Mobile-first: string layouts default to full-width (`2 / -2`) on mobile, apply at `collapsepoint` breakpoint (theme default: "sm").
 * - Layout values are constrained to the content grid area (columns 2 through -2 of a 14-column grid) to allow for consistent gutters and optional bleed on either side.
 * 
 * @param {string|Object} props.layout - Grid column layout for children based on 12 column grid system.
 * 	 Single value examples:
 * 	   - Fraction: "1/3" = each child spans ⅓ of content (4 columns), placed consecutively
 * 	   - Fraction: "1/4" = each child spans ¼ of content (3 columns), placed consecutively
 * 	   - Whole number: "6" = each child spans 6 columns out of 12, placed consecutively  
 * 	   - Percentage: "50%" = each child spans 50% of content (6 columns)
 * 	 Multiple value examples:
 * 	   - Fractions: "1/3 2/3" = children cycle through ⅓ (4 col) and ⅔ (8 col) patterns
 * 	   - Whole numbers: "6 6" = children cycle through 6-column spans
 * 	   - Mixed: "4 8" = children cycle through 4-column and 8-column spans
 * 	 Can be an object with breakpoint keys for responsive behavior (e.g. { xs: "6", md: "1/3" })
 * 	 All layout values are constrained to the content grid area (columns 2 through -2)
 * 
 * @param {string} props.collapsepoint - Breakpoint at which string layouts apply (mobile defaults to full-width below this).
 * 	 Accepts MUI breakpoint keys: "xs", "sm", "md", "lg", "xl", plus "ms" (custom medium-small breakpoint defined in theme).
 * 	 Defaults to theme.Fluid.defaultProps.collapsepoint ("sm" by default).
 * 	 When `bleed` is a responsive object and `layout` is a string, bleed-anchor adjustments
 * 	 take effect at breakpoints at or above this value.
 * 
 * @param {boolean|Object} props.full - If true, grid spans full width of viewport (ignoring max content width).
 *   Accepts a responsive object keyed by breakpoints (e.g. { xs: false, md: true }).
 *
 * @param {"none"|"left"|"right"|"both"|Object} props.bleed - Controls whether children that touch
 *   content edges may extend into bleed columns. Accepts a single value or a responsive object keyed by
 *   breakpoints (e.g. { xs: "none", md: "both" }).
 * 
 * @param {string|React.ElementType} props.component - Element type for each child wrapper. The grid container element is
 *   inferred automatically: "li" → "ul", "dt"/"dd" → "dl", anything else → "div".
 *   Defaults to "div".
 *
 * @param {number|string|false|Object} props.gap - Custom gap between grid cells. Overrides the `--space-gap`
 *   CSS custom property so `gridTemplateColumns` column-width math stays in sync.
 *   - `undefined` (default): inherits `--space-gap` from theme
 *   - `false` or `0`: removes all gaps (`0px`)
 *   - `number`: maps to `theme.spacing(n)` (e.g. `gap={2}` → `"30px"` at desktop breakpoints)
 *   - `string`: passed directly as a CSS value (e.g. `gap="40px"`)
 *   - `object`: responsive breakpoint map (e.g. `gap={{ xs: 0, md: 2 }}`)
 *   Note: if `sx={{ gap: '...' }}` is also set, the CSS `gap` property will be overridden by `sx`,
 *   but `--space-gap` (and therefore column widths) will remain at the `gap` prop value.
 *
 * @param {"start"|"center"|"end"|"space-between"|Object} props.justifyContent - Aligns children within the content area
 *   when a row has unused columns. Always applied to partial last rows; only affects full rows
 *   when the layout span does not divide evenly into 12.
 *   Accepts a responsive object keyed by breakpoints (e.g. { xs: "start", md: "center" }).
 *   - `"start"` (default): children start at the left content edge (column 2)
 *   - `"center"`: centering via `width` + `margin-inline: auto` (single-item rows) or
 *     integer column-offset (multi-item rows). Incompatible with `bleed` — centered items don't
 *     reach either edge so bleed will not activate.
 *   - `"end"`: children pushed to the right content edge via column offset.
 *     Composes correctly with `bleed="right"` — the last item lands exactly at the right edge.
 *   - `"space-between"`: first item anchors to the left edge, last item anchors to the right edge,
 *     middle items distributed evenly with integer rounding. Rows with a single item fall back to `"start"`.
 *     Composes correctly with `bleed` — edge-touching first/last items will extend into bleed columns.
 *
 * @example
 * // Basic usage with a simple layout pattern
 * <Fluid layout="6">
 *   <div>Mobile: full-width | Desktop: columns 2-7 (6 columns)</div>
 *   <div>Mobile: full-width | Desktop: columns 8-13 (6 columns)</div>  
 *   <div>Mobile: full-width | Desktop: wraps to columns 2-7</div>
 * </Fluid>
 * 
 * @example
 * // Layout with 1/3 fraction - each child gets 4-column span (⅓ of 12), placed consecutively
 * <Fluid layout="1/3">
 *   <div>Mobile: full-width | Desktop: columns 2-5 (4 cols)</div>
 *   <div>Mobile: full-width | Desktop: columns 6-9 (4 cols)</div>  
 *   <div>Mobile: full-width | Desktop: columns 10-13 (4 cols, wraps)</div>
 * </Fluid>
 * 
 * @example
 * // Layout with 50% - each child gets 6 columns out of 12
 * <Fluid layout="50%">
 *   <div>Mobile: full-width | Desktop: columns 2-7 (6 columns)</div>
 *   <div>Mobile: full-width | Desktop: columns 8-13 (6 columns)</div>  
 *   <div>Mobile: full-width | Desktop: wraps to columns 2-7</div>
 * </Fluid>
 * 
 * @example  
 * // Layout with multiple patterns - children cycle through patterns on desktop
 * <Fluid layout="1/4 3/4">
 *   <div>Mobile: full-width | Desktop: ¼ pattern (3 cols)</div>
 *   <div>Mobile: full-width | Desktop: ¾ pattern (9 cols)</div>
 *   <div>Mobile: full-width | Desktop: ¼ pattern (3 cols)</div>
 * </Fluid>
 * 
 * @example
 * // Responsive layout - different patterns at different breakpoints
 * <Fluid layout={{ xs: "1", sm: "1/2", md: "1/3" }}>
 *   <div>Mobile: full-width | tablet: 1/2 (6 cols) | desktop: 1/3 (4 cols)</div>
 *   <div>Mobile: full-width | tablet: 1/2 (6 cols) | desktop: 1/3 (4 cols)</div>
 * </Fluid>
 * 
 * @example 
 * // Full width grid - spans entire viewport, ignoring max content width
 * <Fluid layout="1/3" full>
 *  <div>Mobile: full-width | Desktop: ⅓ of viewport (4 cols worth of content width)</div>
 * </Fluid>
 *
 * @example
 * // Edge bleed - edge-touching items can extend into left/right bleed columns
 * <Fluid layout="1/2" bleed="both">
 *   <div>Left item bleeds to left edge</div>
 *   <div>Right item bleeds to right edge</div>
 * </Fluid>
 *
 * @example
 * // Semantic list — children become <li>, container becomes <ul> automatically
 * <Fluid layout="1/3" component="li">
 *   <span>Item 1</span>
 *   <span>Item 2</span>
 *   <span>Item 3</span>
 * </Fluid>
 *
 */


// Sanitize a layout string:
// - Trim leading/trailing whitespace
// - Remove spaces around "/" (e.g. "1 / 4" → "1/4")
// - Condense consecutive spaces to a single space
const sanitizeLayout = (value) =>
	typeof value === 'string'
		? value.trim().replace(/\s*\/\s*/g, '/').replace(/\s{2,}/g, ' ')
		: value;

// Maps a child element type to its semantic container element.
// Used to automatically set the grid container when a semantic child component is specified.
const SEMANTIC_CONTAINER = {
	li: 'ul',
	dt: 'dl', // Good Job Igor
	dd: 'dl', // Good Job Igor
};

const CONTENT_COLUMNS = 12; // 14-column grid: col 1 = left bleed, cols 2–13 = content, col 14 = right bleed
const BLEED_VALUES = ['none', 'left', 'right', 'both'];
const BREAKPOINT_ORDER = ['xs', 'sm', 'ms', 'md', 'lg', 'xl'];

const BLEED_MAP = {
	none: { baseGridColumn: '2 / -2', bleedLeft: false, bleedRight: false },
	left: { baseGridColumn: '1 / -2', bleedLeft: true, bleedRight: false },
	right: { baseGridColumn: '2 / -1', bleedLeft: false, bleedRight: true },
	both: { baseGridColumn: '1 / -1', bleedLeft: true, bleedRight: true },
};

const normalizeBleed = (value) => (BLEED_VALUES.includes(value) ? value : 'none');

const getBleedConfig = (bleed) => {
	const normalizedBleed = normalizeBleed(bleed);
	return BLEED_MAP[normalizedBleed] || BLEED_MAP['none'];
};

// Resolves the effective bleed value at a given breakpoint using mobile-first cascade.
// When bleed is a responsive object, returns the last defined value at or before breakpointKey.
const resolveBleedAtBreakpoint = (bleed, breakpointKey) => {
	if (typeof bleed !== 'object' || bleed === null || Array.isArray(bleed)) {
		return normalizeBleed(bleed);
	}

	let resolvedBleed = 'none';
	for (const key of BREAKPOINT_ORDER) {
		if (bleed[key] !== undefined) {
			resolvedBleed = normalizeBleed(bleed[key]);
		}
		if (key === breakpointKey) {
			break;
		}
	}

	return resolvedBleed;
};

// Mobile-first cascade resolver for any responsive prop (e.g. full, justifyContent).
// If value is a plain scalar, returns it directly. If it's a responsive object, cascades
// down to breakpointKey, returning the last defined value, or fallback if none found.
const resolveResponsiveAt = (value, breakpointKey, fallback) => {
	if (typeof value !== 'object' || value === null || Array.isArray(value)) {
		return value !== undefined ? value : fallback;
	}
	let resolved = fallback;
	for (const key of BREAKPOINT_ORDER) {
		if (value[key] !== undefined) resolved = value[key];
		if (key === breakpointKey) break;
	}
	return resolved;
};

// Resolves the cascaded layout string from a responsive layout object at a given breakpoint.
// Returns null if layout is not a responsive object or no value exists at or before breakpointKey.
const resolveLayoutAtBreakpoint = (layout, breakpointKey) => {
	if (typeof layout !== 'object' || layout === null || Array.isArray(layout)) return null;
	let resolved = null;
	for (const key of BREAKPOINT_ORDER) {
		if (layout[key] !== undefined) resolved = layout[key];
		if (key === breakpointKey) break;
	}
	return resolved;
};

// Resolves the gap prop to a CSS string for use as a --space-gap override.
// - undefined → undefined (no override; default var(--space-gap) from theme stays active)
// - false or 0 → '0px'
// - number → theme.spacing(n)
// - string → passed through as-is
const resolveGapValue = (gap, theme) => {
	if (gap === undefined) return undefined;
	if (gap === false || gap === 0) return '0px';
	if (typeof gap === 'number') return theme.spacing(gap);
	if (typeof gap === 'string') return gap;
	return undefined;
};

// Resolves a layout item string to a column span width (out of maxColumns).
// Supports fractions ("1/3"), percentages ("50%"), whole numbers (6), and numbers > maxColumns as percentages (33).
const resolveSpanWidth = (item, maxColumns) => {
	if (item.includes('/')) {
		const [numerator, denominator] = item.split('/').map(Number);
		if (!numerator || !denominator) return null;
		const span = Math.round(maxColumns * (numerator / denominator));
		return span > 0 ? Math.min(maxColumns, span) : null;
	}
	const isPercent = item.includes('%');
	const num = Number(item.replace('%', ''));
	if (isNaN(num)) return null;
	const span = (isPercent || num > maxColumns) ? Math.round(maxColumns * (num / 100)) : Math.round(num);
	return span > 0 ? Math.min(maxColumns, span) : null;
};

// Groups `count` items (cycling through spanPattern) into rows that respect contentColumns.
const buildRows = (spanPattern, count, contentColumns) => {
	const rows = [];
	let currentRow = [];
	let rowUsed = 0;
	for (let i = 0; i < count; i++) {
		const span = spanPattern[i % spanPattern.length];
		if (rowUsed + span > contentColumns) {
			rows.push(currentRow);
			currentRow = [];
			rowUsed = 0;
		}
		currentRow.push(span);
		rowUsed += span;
		if (rowUsed >= contentColumns) {
			rows.push(currentRow);
			currentRow = [];
			rowUsed = 0;
		}
	}
	if (currentRow.length > 0) rows.push(currentRow);
	return rows;
};

// Returns the grid startCol for each item in a row given the requested alignment.
// space-between distributes leftover between items; center/end apply a uniform row offset; start uses 0.
const computeRowStartCols = (rowSpans, align, contentColumns) => {
	const rowLeftover = contentColumns - rowSpans.reduce((s, x) => s + x, 0);
	const n = rowSpans.length;
	const startCols = [];
	let colPos = 0;
	rowSpans.forEach((span, j) => {
		let startCol;
		if (align === 'space-between' && n > 1 && rowLeftover > 0) {
			startCol = j === n - 1
				? contentColumns + 2 - span // right-anchor to avoid rounding accumulation
				: 2 + colPos + Math.round(j * rowLeftover / (n - 1));
		} else {
			const alignOffset = align === 'center' ? Math.floor(rowLeftover / 2)
				: align === 'end' ? rowLeftover
					: 0;
			startCol = 2 + alignOffset + colPos;
		}
		startCols.push(startCol);
		colPos += span;
	});
	return startCols;
};

// // IGOR_TODO: add unit tests covering all 3 layout paths × 4 alignment modes × bleed × responsive variants.
// Highest regression risk: space-between right-anchor rounding, Path B cycle detection, bleed re-emission.
// Generates layout styles for a given set of layout items.
// Returns flat gridColumn values — mobile-first baseline is set via '> *' in defaultStyle,
// and these styles are wrapped inside a single breakpoint by the caller.
// alignContent shifts column assignments when the layout leaves unused columns per row.
const generateLayoutStyles = (items, bleedValue, childCount, alignContent = 'start') => {
	if (items.length === 0) return {};

	const { bleedLeft, bleedRight } = getBleedConfig(bleedValue);
	const applyEdgeBleed = (startCol, endCol) => {
		const nextStartCol = bleedLeft && startCol === 2 ? 1 : startCol;
		const nextEndCol = bleedRight && endCol === (CONTENT_COLUMNS + 2) ? (CONTENT_COLUMNS + 3) : endCol;
		return { startCol: nextStartCol, endCol: nextEndCol };
	};

	// Writes a gridColumn rule into styles after applying edge bleed.
	const emitRule = (styles, nthExpr, startCol, span) => {
		const { startCol: sc, endCol: ec } = applyEdgeBleed(startCol, startCol + span);
		styles[`> *:nth-of-type(${nthExpr})`] = { gridColumn: `${sc} / ${ec}` };
	};

	if (items.length === 1) {
		// Single layout item: each child gets same span placed consecutively
		const spanWidth = resolveSpanWidth(items[0], CONTENT_COLUMNS);

		if (spanWidth !== null) {
			const itemsPerRow = Math.floor(CONTENT_COLUMNS / spanWidth);

			// center + single item per row: use width + margin-inline: auto for pixel-perfect centering.
			// Avoids integer column-offset drift (e.g. layout="9" → exact 1.5-column margin each side).
			if (alignContent === 'center' && itemsPerRow === 1 && spanWidth < CONTENT_COLUMNS) {
				return {
					'> *:nth-of-type(1n + 1)': {
						gridColumn: '2 / -2',
						width: `calc(${spanWidth} * (100% + var(--space-gap)) / ${CONTENT_COLUMNS} - var(--space-gap))`,
						marginInline: 'auto',
					},
				};
			}

			const styles = {};
			const fullRowStartCols = computeRowStartCols(Array(itemsPerRow).fill(spanWidth), alignContent, CONTENT_COLUMNS);

			// Emit cycling nth-of-type rules — O(itemsPerRow) regardless of child count
			fullRowStartCols.forEach((startCol, k) => {
				emitRule(styles, `${itemsPerRow}n + ${k + 1}`, startCol, spanWidth);
			});

			// Partial last row: emit per-child overrides only where the position differs from the cycling rule.
			const lastRowCount = childCount % itemsPerRow;
			if (lastRowCount > 0 && alignContent !== 'start') {
				const lastRowStartCols = computeRowStartCols(Array(lastRowCount).fill(spanWidth), alignContent, CONTENT_COLUMNS);
				const firstLastRowChildIndex = childCount - lastRowCount + 1;
				lastRowStartCols.forEach((startCol, j) => {
					if (startCol !== fullRowStartCols[j]) {
						emitRule(styles, `${firstLastRowChildIndex + j}`, startCol, spanWidth);
					}
				});
			}

			return styles;
		} else {
			// Non-numeric format, use as-is
			return { '> *': { gridColumn: items[0] } };
		}
	} else {
		// Multiple layout items: children repeat pattern sequentially and wrap on overflow
		const spans = items.map((item) => resolveSpanWidth(item, CONTENT_COLUMNS));

		if (spans.every((span) => span !== null)) {
			// Detect whether this layout cycles cleanly back to (spanIdx=0, usedColumns=0).
			// Clean layouts (e.g., "1/3 2/3", "4 8") cycle within the cap → Path A.
			// Irregular layouts (e.g., "9 1") that never return to origin → Path B.
			const cap = spans.length * CONTENT_COLUMNS;
			const cycleSpans = [];
			let usedColumns = 0;
			let cycleFound = false;

			for (let i = 0; i < cap; i++) {
				const spanIdx = i % spans.length;
				const spanWidth = spans[spanIdx];

				if (usedColumns + spanWidth > CONTENT_COLUMNS) {
					usedColumns = 0;
				}

				cycleSpans.push(spanWidth);

				usedColumns += spanWidth;
				if (usedColumns >= CONTENT_COLUMNS) {
					usedColumns = 0;
				}

				// Cycle complete when the last span in the pattern lands us back at column 0
				if (spanIdx === spans.length - 1 && usedColumns === 0) {
					cycleFound = true;
					break;
				}
			}

			if (cycleFound) {
				// Build and emit cycling rules in a single pass using the collected spans.
				const cycleLen = cycleSpans.length;
				const styles = {};
				let idx = 0;
				buildRows(cycleSpans, cycleLen, CONTENT_COLUMNS).forEach((rowSpans) => {
					computeRowStartCols(rowSpans, alignContent, CONTENT_COLUMNS).forEach((startCol, j) => {
						const { startCol: sc, endCol: ec } = applyEdgeBleed(startCol, startCol + rowSpans[j]);
						styles[`> *:nth-of-type(${cycleLen}n + ${idx + 1})`] = { gridColumn: `${sc} / ${ec}` };
						idx++;
					});
				});
				return styles;
			}

			// Path B: irregular layout — emit one rule per child (uses childCount)
			const styles = {};
			let childIndex = 0;
			buildRows(spans, childCount, CONTENT_COLUMNS).forEach((rowSpans) => {
				computeRowStartCols(rowSpans, alignContent, CONTENT_COLUMNS).forEach((startCol, j) => {
					emitRule(styles, `${childIndex + 1}`, startCol, rowSpans[j]);
					childIndex++;
				});
			});
			return styles;
		}

		// Non-numeric patterns fall back to nth-of-type cycle behavior
		return items.reduce((acc, item, index) => {
			acc[`> *:nth-of-type(${items.length}n + ${index + 1})`] = { gridColumn: item };
			return acc;
		}, {});
	}
};

// Nested grids live inside a parent cell that is already the content width, so they get a plain
// 12-track template (no bleed tracks). Column placements computed for the 14-track grid are shifted by one.
const NESTED_TEMPLATE = 'repeat(var(--col-count), minmax(0, 1fr))';
const shiftLine = (n) => (n > 0 ? Math.min(Math.max(n - 1, 1), CONTENT_COLUMNS + 1) : -1);
const shiftColumnValue = (value) =>
	typeof value === 'string' && /^\s*-?\d+\s*\/\s*-?\d+\s*$/.test(value)
		? value.split('/').map((part) => shiftLine(Number(part))).join(' / ')
		: value;
const shiftNestedStyles = (styles) => {
	const out = {};
	Object.keys(styles).forEach((key) => {
		const val = styles[key];
		if (key === 'gridColumn') out[key] = shiftColumnValue(val);
		else if (key === 'gridTemplateColumns') out[key] = NESTED_TEMPLATE;
		else if (val && typeof val === 'object') out[key] = shiftNestedStyles(val);
		else out[key] = val;
	});
	return out;
};

// Holds the parent Fluid's bleed (null when there is no parent Fluid).
const FluidNestContext = React.createContext(null);

const StyledGrid = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'childCount' && prop !== 'layout' && prop !== 'full' && prop !== 'collapsepoint' && prop !== 'bleed' && prop !== 'gap' && prop !== 'justifyContent' && prop !== 'isNested' && prop !== 'parentBleed',
})(({ theme, layout = "12", childCount = 0, full = false, collapsepoint = 'sm', bleed = 'none', gap = undefined, justifyContent = 'start', isNested: isNestedProp = false, parentBleed = 'none', }) => {
	const isNested = isNestedProp;
	const baseBleed = resolveBleedAtBreakpoint(bleed, 'xs');
	const baseFull = resolveResponsiveAt(full, 'xs', false);
	const resolvedGap = typeof gap !== 'object' ? resolveGapValue(gap, theme) : undefined;

	const sanitizedLayout = sanitizeLayout(layout);
	const layoutItems = typeof sanitizedLayout === 'string' ? sanitizedLayout.split(' ').map(item => item.trim()) : (Array.isArray(sanitizedLayout) ? sanitizedLayout : []);

	const defaultStyle = {
		display: 'grid',
		// Grid Template Breakdown:
		// 1. Start with a full-width column for left bleed (1fr)
		// 2. Then repeat the content columns based on --col-count, calculating each column's width as a fraction of the available content space (min of max width or viewport minus padding)
		// 3. End with another full-width column for right bleed (1fr)
		gridTemplateColumns: `1fr repeat(var(--col-count), calc((min(100% - var(--space-lr), ${baseFull ? 'var(--row-max-width-full)' : 'var(--row-max-width)'}) - (var(--col-count) - 1) * var(--space-gap)) / var(--col-count))) 1fr`, ...(resolvedGap !== undefined && { '--space-gap': resolvedGap }),
		gap: resolvedGap ?? 'var(--space-gap)',
		// Default: all children span full content width (mobile-first baseline)
		'> *': {
			gridColumn: getBleedConfig(baseBleed).baseGridColumn,
		},
		// When a string layout is passed, apply it at breakpoint (mobile defaults to full-width above)
		...(typeof layout === 'string' && layoutItems.length > 0 && {
			[theme.breakpoints.up(collapsepoint)]: generateLayoutStyles(layoutItems, resolveBleedAtBreakpoint(bleed, collapsepoint), childCount, resolveResponsiveAt(justifyContent, collapsepoint, 'start')),
		}),
	}

	if (typeof bleed === 'object' && bleed !== null && !Array.isArray(bleed)) {
		Object.keys(bleed).forEach((key) => {
			const responsiveBleedColumn = getBleedConfig(resolveBleedAtBreakpoint(bleed, key)).baseGridColumn;
			defaultStyle[theme.breakpoints.up(key)] = {
				...defaultStyle[theme.breakpoints.up(key)],
				'> *': {
					...defaultStyle[theme.breakpoints.up(key)]?.['> *'],
					gridColumn: responsiveBleedColumn,
				},
			};
		});

		// When layout is a string, nth-of-type rules are initially emitted only at collapsepoint.
		// If bleed changes at a later breakpoint, those rules must be re-emitted with updated bleed
		// anchors — otherwise the more-specific nth-of-type selectors silently override the > * update.
		if (typeof layout === 'string' && layoutItems.length > 0) {
			Object.keys(bleed).forEach((key) => {
				if (BREAKPOINT_ORDER.indexOf(key) >= BREAKPOINT_ORDER.indexOf(collapsepoint)) {
					const reEmittedStyles = generateLayoutStyles(layoutItems, resolveBleedAtBreakpoint(bleed, key), childCount, resolveResponsiveAt(justifyContent, key, 'start'));
					if (Object.keys(reEmittedStyles).length > 0) {
						defaultStyle[theme.breakpoints.up(key)] = {
							...defaultStyle[theme.breakpoints.up(key)],
							...reEmittedStyles,
						};
					}
				}
			});
		}
	}

	// Handle responsive full objects — override gridTemplateColumns at each breakpoint where full changes.
	if (typeof full === 'object' && full !== null && !Array.isArray(full)) {
		Object.keys(full).forEach((key) => {
			const resolvedFull = resolveResponsiveAt(full, key, false);
			defaultStyle[theme.breakpoints.up(key)] = {
				...defaultStyle[theme.breakpoints.up(key)],
				gridTemplateColumns: `1fr repeat(var(--col-count), calc((min(100% - var(--space-lr), ${resolvedFull ? 'var(--row-max-width-full)' : 'var(--row-max-width)'}) - (var(--col-count) - 1) * var(--space-gap)) / var(--col-count))) 1fr`,
			};
		});
	}

	// When justifyContent is responsive and layout is a string, re-emit nth-of-type rules at each
	// justifyContent breakpoint >= collapsepoint with the updated alignment value.
	if (typeof layout === 'string' && layoutItems.length > 0 && typeof justifyContent === 'object' && justifyContent !== null) {
		Object.keys(justifyContent).forEach((key) => {
			if (BREAKPOINT_ORDER.indexOf(key) >= BREAKPOINT_ORDER.indexOf(collapsepoint)) {
				const styles = generateLayoutStyles(layoutItems, resolveBleedAtBreakpoint(bleed, key), childCount, resolveResponsiveAt(justifyContent, key, 'start'));
				if (Object.keys(styles).length > 0) {
					defaultStyle[theme.breakpoints.up(key)] = {
						...defaultStyle[theme.breakpoints.up(key)],
						...styles,
					};
				}
			}
		});
	}

	// Handle responsive layout objects (mobile-first: xs = full-width baseline, each key applies at bp+)
	if (typeof layout === 'object' && !Array.isArray(layout)) {
		Object.keys(layout).forEach((key) => {
			const sanitizedValue = sanitizeLayout(String(layout[key]));
			const responsiveLayoutItems = sanitizedValue.split(' ').map(item => item.trim());
			const responsiveLayoutStyles = generateLayoutStyles(responsiveLayoutItems, resolveBleedAtBreakpoint(bleed, key), childCount, resolveResponsiveAt(justifyContent, key, 'start'));

			if (Object.keys(responsiveLayoutStyles).length > 0) {
				defaultStyle[theme.breakpoints.up(key)] = {
					...defaultStyle[theme.breakpoints.up(key)],
					...responsiveLayoutStyles,
				};
			}
		});

		// Re-emit at each justifyContent breakpoint using the cascaded layout value — handles the case
		// where justifyContent changes at a breakpoint not present in the layout object.
		if (typeof justifyContent === 'object' && justifyContent !== null) {
			Object.keys(justifyContent).forEach((key) => {
				const cascadedLayout = resolveLayoutAtBreakpoint(layout, key);
				if (cascadedLayout !== null) {
					const sanitizedValue = sanitizeLayout(String(cascadedLayout));
					const responsiveLayoutItems = sanitizedValue.split(' ').map(item => item.trim());
					const styles = generateLayoutStyles(responsiveLayoutItems, resolveBleedAtBreakpoint(bleed, key), childCount, resolveResponsiveAt(justifyContent, key, 'start'));
					if (Object.keys(styles).length > 0) {
						defaultStyle[theme.breakpoints.up(key)] = {
							...defaultStyle[theme.breakpoints.up(key)],
							...styles,
						};
					}
				}
			});
		}
	}

	// Handle responsive gap objects (mobile-first: each key overrides --space-gap at that breakpoint)
	if (typeof gap === 'object' && gap !== null && !Array.isArray(gap)) {
		Object.keys(gap).forEach((key) => {
			const resolvedVal = resolveGapValue(gap[key], theme);
			if (resolvedVal !== undefined) {
				defaultStyle[theme.breakpoints.up(key)] = {
					...defaultStyle[theme.breakpoints.up(key)],
					'--space-gap': resolvedVal,
					gap: resolvedVal,
				};
			}
		});
	}

	if (!isNested) return defaultStyle;

	// Nested adjustments are breakpoint-aware: wherever this Fluid's bleed or its parent's bleed resolves
	// to "both" (the parent cell is then full-viewport), the normal 14-track grid applies.
	const nestedAt = (bp) => resolveBleedAtBreakpoint(bleed, bp) !== 'both' && resolveBleedAtBreakpoint(parentBleed, bp) !== 'both';
	const fullTemplateAt = (key) => `1fr repeat(var(--col-count), calc((min(100% - var(--space-lr), ${resolveResponsiveAt(full, key, false) ? 'var(--row-max-width-full)' : 'var(--row-max-width)'}) - (var(--col-count) - 1) * var(--space-gap)) / var(--col-count))) 1fr`;
	const keysOf = (b) => (typeof b === 'object' && b !== null ? Object.keys(b) : []);
	const bleedKeys = [...keysOf(bleed), ...keysOf(parentBleed)];
	const mediaToKey = {};
	BREAKPOINT_ORDER.forEach((k) => { mediaToKey[theme.breakpoints.up(k)] = k; });

	const result = {};
	const baseNested = nestedAt('xs');
	Object.keys(defaultStyle).forEach((key) => {
		const val = defaultStyle[key];
		const bp = mediaToKey[key];
		if (bp === undefined) {
			if (key === 'maxWidth') { if (baseNested) result[key] = val; return; }
			result[key] = baseNested ? shiftNestedStyles({ [key]: val })[key] : val;
			return;
		}
		const nestedHere = nestedAt(bp);
		let entry = nestedHere ? shiftNestedStyles(val) : { ...val };
		if (bleedKeys.includes(bp) && nestedHere !== baseNested) {
			entry.gridTemplateColumns = nestedHere ? NESTED_TEMPLATE : fullTemplateAt(bp);
		}
		result[key] = entry;
	});
	if (baseNested) result.maxWidth = '100%';
	return result;
});

export const Fluid = React.forwardRef((inProps, ref) => {
	const { children, layout, collapsepoint, bleed, gap, justifyContent, component = 'div', isNested: isNestedProp, ...props } = useThemeProps({ props: inProps, name: 'Fluid' });

	const parentContext = React.useContext(FluidNestContext);
	const isNested = isNestedProp ?? (parentContext !== null);
	const parentBleed = parentContext ?? 'none';
	const childArray = React.Children.toArray(children);
	const allSameType = childArray.length > 0 && childArray.every((child) => {
		if (!React.isValidElement(child) || typeof child.type !== 'string') {
			return false;
		}

		return child.type === childArray[0].type;
	});

	const uniformChildType = allSameType ? childArray[0].type : null;
	const containerComponent = (uniformChildType && SEMANTIC_CONTAINER[uniformChildType]) ?? SEMANTIC_CONTAINER[component] ?? 'div';
	const wrappedChildren = allSameType
		? children
		: React.Children
			.map(children, (child) => {
				// check if child is a Fluid, if so set isNested to true
				if (React.isValidElement(child)) {
					const ChildWrapper = component;
					const Fluid = child.type && child.type.displayName === 'Fluid';
					if (Fluid) {
						child = React.cloneElement(child, { isNested: true });
					}
					return (
						<ChildWrapper>
							{child}
						</ChildWrapper>
					);
				}
				return null;
			});

	return (
		<FluidNestContext.Provider value={bleed ?? 'none'}>
		<StyledGrid ref={ref} component={containerComponent} layout={layout} collapsepoint={collapsepoint} bleed={bleed} gap={gap} justifyContent={justifyContent} isNested={isNested} parentBleed={parentBleed} childCount={React.Children.count(children)} {...props}>
			{wrappedChildren}
		</StyledGrid>
		</FluidNestContext.Provider>
	);
});

Fluid.displayName = 'Fluid';

/**
 * PropTypes validation for the Fluid component
 */
Fluid.propTypes = {
	/**
	 * Grid column layout for children based on 12 column grid system.
	 * Mobile-first: defaults to full-width on mobile, applies layout at `collapsepoint` breakpoint (theme default: "sm").
	 * 
	 * Format options:
	 *   - Fractions: "1/3" (4 cols), "1/4" (3 cols), "2/3" (8 cols) — mathematical division
	 *   - Percentages: "50%" (6 cols), "33%" (~4 cols)
	 *   - Whole numbers: "6" (6 cols), "4" (4 cols)
	 *   - Numbers > 12: "33" (treated as 33% → 4 cols)
	 * 
	 * Multiple values cycle through patterns: "1/3 2/3" or "4 8"
	 * Responsive: object with breakpoint keys { xs: "1", sm: "1/2", md: "1/3" }
	 * All values constrained within content grid (columns 2–13 of 14-column grid).
	 */
	layout: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.object
	]),
	/**
	 * Breakpoint at which string layouts apply (mobile defaults to full-width below this).
	 * Accepts MUI breakpoint keys: "xs", "sm", "md", "lg", "xl", plus "ms" (custom medium-small breakpoint defined in theme).
	 * Defaults to theme.Fluid.defaultProps.collapsepoint ("sm" by default).
	 */
	collapsepoint: PropTypes.oneOf(['xs', 'sm', 'ms', 'md', 'lg', 'xl']),
	bleed: PropTypes.oneOfType([
		PropTypes.oneOf(BLEED_VALUES),
		PropTypes.shape({
			xs: PropTypes.oneOf(BLEED_VALUES),
			sm: PropTypes.oneOf(BLEED_VALUES),
			ms: PropTypes.oneOf(BLEED_VALUES),
			md: PropTypes.oneOf(BLEED_VALUES),
			lg: PropTypes.oneOf(BLEED_VALUES),
			xl: PropTypes.oneOf(BLEED_VALUES),
		}),
	]),
	full: PropTypes.oneOfType([
		PropTypes.bool,
		PropTypes.shape({
			xs: PropTypes.bool,
			sm: PropTypes.bool,
			ms: PropTypes.bool,
			md: PropTypes.bool,
			lg: PropTypes.bool,
			xl: PropTypes.bool,
		}),
	]),
	/**
	 * Element type for each child wrapper. The grid container is inferred automatically:
	 * "li" → "ul", "dt"/"dd" → "dl", anything else → "div".
	 * Defaults to "div".
	 * Note: If all children are the same semantic type (e.g., all <li>), the container will be set to the appropriate semantic parent (<ul>) regardless of this prop, and the children will not be wrapped. 
	 * If children are mixed types, they will be wrapped in the specified component (default "div") and the container will be determined by that component or default to "div" if it has no semantic container mapping.
	 */
	component: PropTypes.elementType,
	/**
	 * Aligns children within the content area when a row has unused columns.
	 * Always applied to partial last rows; only affects full rows for layouts that do not divide evenly into 12 (e.g. "9", "7", "5").
	 * - "start" (default): left content edge
	 * - "center": pixel-perfect centering (single-item rows) or column-offset (multi-item rows)
	 * - "end": right content edge; composes with bleed="right"
	 * - "space-between": first item left-anchored, last item right-anchored, middle items distributed; single-item rows fall back to "start"; composes with bleed
	 */
	justifyContent: PropTypes.oneOfType([
		PropTypes.oneOf(['start', 'center', 'end', 'space-between']),
		PropTypes.shape({
			xs: PropTypes.oneOf(['start', 'center', 'end', 'space-between']),
			sm: PropTypes.oneOf(['start', 'center', 'end', 'space-between']),
			ms: PropTypes.oneOf(['start', 'center', 'end', 'space-between']),
			md: PropTypes.oneOf(['start', 'center', 'end', 'space-between']),
			lg: PropTypes.oneOf(['start', 'center', 'end', 'space-between']),
			xl: PropTypes.oneOf(['start', 'center', 'end', 'space-between']),
		}),
	]),
	/**
	 * Custom gap override. Accepts number (theme.spacing units), CSS string, false/0 (no gap),
	 * or a responsive breakpoint object (e.g. { xs: 0, md: 2 }).
	 */
	gap: PropTypes.oneOfType([
		PropTypes.number,
		PropTypes.string,
		PropTypes.oneOf([false]),
		PropTypes.object,
	]),
	children: PropTypes.node.isRequired,
};

const MemoizedFluid = React.memo(Fluid);
MemoizedFluid.displayName = 'Fluid';

export default MemoizedFluid;